import { gsap } from "gsap";
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
import { onWidthResize, offWidthResize } from '../utils/resize.js';
gsap.registerPlugin(ScrollTrigger, SplitText);

export default class HeroAnimated {
    constructor(container) {
        this.container = container;
        this.title = this.container.querySelector('.hero-animated__title');
        this.init();
    }

    init = () => {
        // Desktop only: below md the title animates in with CSS before JS
        // arrives (see hero-animated.scss), so splitting it here would replay it.
        if (window.matchMedia('(min-width: 1024px)').matches) this.animateTitle();

        new ImageMouseTrail(this.container);
    }

    animateTitle = () => {
        this.text_lines = new SplitText(this.title, { type: 'lines,chars', linesClass: "lineParent" });
        gsap.fromTo(this.text_lines.chars, { yPercent: 100, }, {
            yPercent: 0, duration: 0.6, delay: 0.6, stagger: { each: 0.01, ease: 'power.out', from: 'start' }, ease: 'power3.out', onComplete: () => {
                onWidthResize(this.onResize);
            }
        });
    }


    onResize = () => {
        this.text_lines.revert();
        offWidthResize(this.onResize);
    }
}

class ImageMouseTrail {
    constructor(container) {
        this.options = { el: container };
        // Images are position:absolute inside this relative wrapper, so
        // pointer coordinates must be relative to it — not event.target.
        this.trailEl = container.querySelector('.hero-animated') || container;

        this.images = [];
        this.images_elems = this.options.el.querySelectorAll('.hero-animated__item');
        [...this.images_elems].forEach(img => this.images.push(new Image(img)));

        this.imagesTotal = this.images.length;
        // Liquid duplicates the unique set for the cursor-trail pool. Auto-play
        // only reveals unique images, capped so the collage stays airy.
        this.uniqueImages = this.images.filter((img) => !img.options.el.hasAttribute('data-clone'));
        this.autoImagesTotal = Math.min(4, this.uniqueImages.length || this.imagesTotal);

        this.imgPosition = 0;
        this.zIndexVal = 1;
        this.tracking = false;
        this.lastSpawnTime = -Infinity;
        this.minSpawnInterval = 0.05;
        this.threshold = Math.max(80, (this.images_elems[0]?.offsetWidth || 240) - 100);

        this.MathUtils = {
            lerp: (a, b, n) => (1 - n) * a + n * b,
            distance: (x1, y1, x2, y2) => Math.hypot(x2 - x1, y2 - y1)
        }

        this.mousePos = { x: 0, y: 0 };
        this.lastMousePos = { x: 0, y: 0 };
        this.cacheMousePos = { x: 0, y: 0 };

        this.getMouseDistance = () => this.MathUtils.distance(this.mousePos.x, this.mousePos.y, this.lastMousePos.x, this.lastMousePos.y);

        gsap.set(this.images_elems, { opacity: 0, scale: 0 });

        // Touch/no-hover devices never fire mousemove, so drive the same trail
        // logic with a synthetic pointer that wanders the empty top area.
        this.isTouch = window.matchMedia('(hover: none)').matches;

        window.addEventListener('pageshow', this.onPageShow);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        if (this.isTouch) {
            this.startAutoTrail();
            this.observeVisibility();
        } else {
            this.options.el.addEventListener('mouseenter', this.onMouseEnter);
            this.options.el.addEventListener('mouseleave', this.onMouseLeave);
            this.options.el.addEventListener('mousemove', this.onMouseMove);
        }
    }

    // --- Auto (touch) mode -------------------------------------------------

    startAutoTrail = () => {
        this.stopAutoTrail();
        this.autoIndex = 0;
        this.roundPoints = [];
        this.playRound();
    }

    // Reveal unique images one by one, letting them build up; hold briefly,
    // then clear and restart the loop.
    playRound = () => {
        const point = this.getSpacedPoint();
        this.roundPoints.push(point);
        this.autoRevealImage(this.autoIndex, point);
        this.autoIndex++;

        if (this.autoIndex >= this.autoImagesTotal) {
            this.autoCall = gsap.delayedCall(1.6, () => {
                this.clearImages();
                this.autoIndex = 0;
                this.roundPoints = [];
                this.autoCall = gsap.delayedCall(1.2, this.playRound);
            });
        } else {
            // Keep a full round around ~4s even with the smaller unique set.
            const interval = gsap.utils.clamp(0.55, 1, 4 / this.autoImagesTotal);
            this.autoCall = gsap.delayedCall(interval, this.playRound);
        }
    }

    // A random top-band point kept away from the images already placed this
    // round, so they spread out instead of bunching up. Falls back to the
    // most-spaced candidate when the band is too tight to fully satisfy it.
    getSpacedPoint = () => {
        const minDist = Math.max(80, (this.images_elems[0]?.offsetWidth || 240) * 1.15);
        let best = this.getRandomTopPoint();
        let bestDist = -1;
        for (let i = 0; i < 18; i++) {
            const p = this.getRandomTopPoint();
            const d = this.roundPoints.length
                ? Math.min(...this.roundPoints.map((rp) => this.MathUtils.distance(rp.x, rp.y, p.x, p.y)))
                : Infinity;
            if (d >= minDist) return p;
            if (d > bestDist) { best = p; bestDist = d; }
        }
        return best;
    }

    // Auto-mode reveal: the image drifts in from a small offset and stays put
    // (it's cleared later by clearImages, not faded per-image like the cursor).
    autoRevealImage = (index, point) => {
        const img = this.uniqueImages[index] || this.images[index];
        if (!img) return;
        img.getRect();
        const halfW = (img.rect.width || 280) / 2;
        const halfH = (img.rect.height || 280) / 2;
        gsap.killTweensOf(img.options.el);
        const startX = point.x + gsap.utils.random(-40, 40);
        const startY = point.y + gsap.utils.random(-40, 40);
        gsap.timeline()
            .set(img.options.el, {
                opacity: 1,
                scale: 0.6,
                rotation: gsap.utils.random(-6, 6),
                zIndex: this.zIndexVal++,
                x: startX - halfW,
                y: startY - halfH,
            })
            .to(img.options.el, {
                duration: 0.7,
                ease: 'expo.out',
                scale: 1,
                x: point.x - halfW,
                y: point.y - halfH,
            });
    }

    // Fade/scale the unique set out (end of a round) before it restarts.
    clearImages = () => {
        const els = (this.uniqueImages.length ? this.uniqueImages : this.images).map((img) => img.options.el);
        gsap.to(els, {
            duration: 0.5,
            ease: 'back.in',
            scale: 0,
            stagger: { each: 0.05, from: 'random' },
            onComplete: () => gsap.set(els, { opacity: 0 }),
        });
    }

    // Random point confined to the empty top band above the title, padded so
    // images don't clip the section edges.
    getRandomTopPoint = () => {
        const pad = 90;
        const w = this.options.el.clientWidth;
        const h = this.options.el.clientHeight;
        return {
            x: gsap.utils.random(pad, Math.max(pad + 1, w - pad)),
            y: gsap.utils.random(pad, Math.max(pad + 1, h * 0.55))
        };
    }

    observeVisibility = () => {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.resumeAutoTrail();
                } else {
                    this.pauseAutoTrail();
                }
            });
        }, { threshold: 0 });
        this.observer.observe(this.options.el);
    }

    resumeAutoTrail = () => {
        if (this.autoCall) this.autoCall.play();
        else this.startAutoTrail();
    }

    pauseAutoTrail = () => {
        this.autoCall?.pause();
    }

    stopAutoTrail = () => {
        this.autoCall?.kill();
        this.autoCall = null;
    }

    // Checkout (and other cross-origin navigations) restores this page from
    // bfcache. GSAP delayedCalls catch up and the browser fires a burst of
    // mousemove events against a stale last position — the trail dumps every
    // image in one place. Reset and wait for a fresh pointer sample.
    onPageShow = (e) => {
        if (!e.persisted) return;
        this.stopAutoTrail();
        this.resetTrail();
        if (this.isTouch) this.startAutoTrail();
    }

    onVisibilityChange = () => {
        if (document.hidden) {
            this.stopTracking();
        }
    }

    resetTrail = () => {
        this.stopTracking();
        gsap.killTweensOf(this.images_elems);
        gsap.set(this.images_elems, { opacity: 0, scale: 0 });
        this.zIndexVal = 1;
        this.imgPosition = 0;
        this.lastSpawnTime = -Infinity;
    }

    copyPos = (pos) => ({ x: pos.x, y: pos.y })

    startTracking = () => {
        if (this.tracking) return;
        this.lastMousePos = this.copyPos(this.mousePos);
        this.cacheMousePos = this.copyPos(this.mousePos);
        this.tracking = true;
        gsap.ticker.add(this.render);
    }

    stopTracking = () => {
        this.tracking = false;
        gsap.ticker.remove(this.render);
    }

    onMouseEnter = (ev) => {
        this.readPointer(ev);
        this.startTracking();
    }

    onMouseLeave = () => {
        this.stopTracking();
    }

    onMouseMove = (ev) => {
        this.readPointer(ev);
        this.startTracking();
    }

    readPointer = (ev) => {
        const rect = this.trailEl.getBoundingClientRect();
        this.mousePos = {
            x: ev.clientX - rect.left,
            y: ev.clientY - rect.top,
        };
    }

    render = () => {
        if (!this.tracking) return;

        this.distance = this.getMouseDistance();
        this.cacheMousePos.x = this.MathUtils.lerp(this.cacheMousePos.x, this.mousePos.x, 0.3);
        this.cacheMousePos.y = this.MathUtils.lerp(this.cacheMousePos.y, this.mousePos.y, 0.3);

        const now = gsap.ticker.time;
        if (this.distance > this.threshold && (now - this.lastSpawnTime) >= this.minSpawnInterval) {
            this.lastSpawnTime = now;
            this.showNextImage();
            ++this.zIndexVal;

            this.imgPosition = this.imgPosition < this.imagesTotal - 1 ? this.imgPosition + 1 : 0;
            this.lastMousePos = this.copyPos(this.mousePos);
        }

        let isIdle = true;
        for (let img of this.images) {
            if (img.isActive()) {
                isIdle = false;
                break;
            }
        }

        if (isIdle && this.zIndexVal !== 1) {
            this.zIndexVal = 1;
        }
    }
    showNextImage() {
        const img = this.images[this.imgPosition];
        if (!img) return;

        img.getRect();
        const halfW = (img.rect.width || 280) / 2;
        const halfH = (img.rect.height || 280) / 2;

        gsap.killTweensOf(img.options.el);

        gsap.timeline()
            .set(img.options.el, { opacity: 1, scale: 1, zIndex: this.zIndexVal, x: this.cacheMousePos.x - halfW, y: this.cacheMousePos.y - halfH }, 0)
            .to(img.options.el, { duration: 0.9, ease: 'expo.out', rotate: gsap.utils.random(-5, 5), x: this.mousePos.x - halfW, y: this.mousePos.y - halfH }, 0)
            .to(img.options.el, { duration: 0.7, ease: 'back.in', scale: 0 }, 0.4);
    }
}

class Image {
    constructor(el) {
        this.options = { el: el, style: { scale: 1, x: 0, y: 0, opacity: 0 } };
        this.getRect();
        this.initEvents();
    }
    initEvents() {
        // on resize get updated sizes/position
        //window.addEventListener('resize', () => this.resize());
    }
    resize() {
        gsap.set(this.options.el, this.options.style);
        this.getRect();
    }
    getRect() {
        // offsetWidth ignores transforms, so this stays valid while the
        // trail scales an item to 0. getBoundingClientRect() would be 0×0.
        this.rect = {
            width: this.options.el.offsetWidth,
            height: this.options.el.offsetHeight,
        };
    }
    isActive() {
        return gsap.isTweening(this.options.el) || this.options.el.style.opacity != 0;
    }
}